import type { PGD } from "../types";

export const pgdAmoxicillinAom: PGD = {
  id: "pgd-7b-amox-aom",
  reference: "7b",
  version: "1.0",
  drugRef: "amoxicillin",
  indication:
    "Acute otitis media — no penicillin allergy. Antibiotic-eligible patients only.",
  validFrom: "2024-01-31",
  expiryDate: "2027-01-30",
  frameworkType: "pharmacy_first",
  population: {
    ageRange: { min: 1, max: 17 },
    pregnancyAllowed: "yes_if_16_plus",
    breastfeedingAllowed: true,
    notes: "TDS dosing (not QDS). 1-4yr: 250mg TDS. 5-17yr: 500mg TDS.",
  },
  duration: { days: 5 },
  doseBands: [
    {
      id: "amox-age1-4",
      resolveBy: "age",
      ageRange: { min: 1, max: 4 },
      dose: 250,
      frequency: "THREE times a day",
      formulations: [
        { formulationId: "amox-500-5ml", instruction: "Give 2.5mL (250mg) THREE times a day", quantity: { bottles: 1, label: "1 × 100mL bottle" } },
        { formulationId: "amox-250-5ml", instruction: "Give 5mL (250mg) THREE times a day", quantity: { bottles: 1, label: "1 × 100mL bottle" } },
        { formulationId: "amox-125-5ml", instruction: "Give 10mL (250mg) THREE times a day", quantity: { bottles: 2, label: "2 × 100mL bottles" } },
      ],
    },
    {
      id: "amox-age5-17",
      resolveBy: "age",
      ageRange: { min: 5, max: 17 },
      dose: 500,
      frequency: "THREE times a day",
      formulations: [
        { formulationId: "amox-500-cap", instruction: "Take ONE 500mg capsule THREE times a day", quantity: { count: 15, label: "15 capsules" } },
        { formulationId: "amox-250-cap", instruction: "Take TWO 250mg capsules THREE times a day", quantity: { count: 30, label: "30 capsules" } },
        { formulationId: "amox-500-5ml", instruction: "Give 5mL (500mg) THREE times a day", quantity: { bottles: 1, label: "1 × 100mL bottle" } },
        { formulationId: "amox-250-5ml", instruction: "Give 10mL (500mg) THREE times a day", quantity: { bottles: 2, label: "2 × 100mL bottles" } },
        { formulationId: "amox-125-5ml", instruction: "Give 20mL (500mg) THREE times a day", quantity: { bottles: 3, label: "3 × 100mL bottles" } },
      ],
    },
  ],
  preSupplyChecks: [
    { id: "ex-consent-refused", question: "Has the patient/carer refused consent?" },
    { id: "ex-unable-swallow", question: "Unable to absorb oral medications and/or unable to swallow oral dosage formulations?" },
    { id: "ex-long-term-amox", question: "Currently on long-term amoxicillin or amoxicillin-containing preparation?", hint: "e.g. prophylaxis" },
    { id: "ex-keto", question: "Ketogenic diet?" },
    { id: "ex-failed-abx", question: "Failed previous antibiotic for this episode of acute otitis media?" },
    { id: "ex-foreign-body", question: "Evidence of, or suspected, foreign body in the ear canal?" },
    { id: "ex-glue-ear", question: "Otitis media with visible effusion (glue ear)?" },
    { id: "ex-temp-39", question: "Temperature over 39°C?", linkedVitalType: "temperature" },
    { id: "ex-cholesteatoma", question: "Known cholesteatoma?" },
    { id: "ex-bloody-discharge", question: "Bloody or blood-stained discharge from ear?", comment: "Possible cancer — refer urgently" },
    { id: "ex-ckd-30", question: "Known CKD stage 4 or 5 (eGFR <30 mL/min/1.73m²)?" },
    { id: "ex-typhoid", question: "Oral typhoid vaccine within ±3 days?" },
    { id: "int-allopurinol", question: "Taking allopurinol?", comment: "Increased risk of rash with amoxicillin" },
    { id: "int-methotrexate", question: "Taking methotrexate?" },
    { id: "int-probenecid", question: "Taking probenecid?" },
    { id: "int-warfarin", question: "Taking warfarin or other coumarin anticoagulant?" },
  ],
  cautions: [
    { id: "caution-breastfeeding", text: "Breastfeeding — monitor nursing infant for GI disturbances, oral candida, rashes, irritability and drowsiness." },
    { id: "caution-excipients", text: "Check excipients: lactose, sucrose, fructose, sorbitol (hereditary intolerances); aspartame (phenylketonuria)" },
  ],
  counselling: [
    "Take with food or water",
    "Capsules should be swallowed whole",
    "THREE times a day — space doses evenly through the day (e.g. morning, midday, evening)",
    "Complete the full 5-day course even if symptoms improve",
    "Symptoms should start to improve within 72 hours — seek medical advice if not improving or worsening",
    "Paracetamol or ibuprofen (OTC, weight-appropriate dose) should also be used regularly for pain",
    "Decongestants and antihistamines do NOT help with AOM symptoms",
    "Seek urgent help (999/A&E) if signs of sepsis or serious complications develop",
  ],
  counsellingPoints: [
    { text: "Take with food or water" },
    { text: "Capsules should be swallowed whole" },
    { text: "THREE times a day — space doses evenly through the day (e.g. morning, midday, evening)" },
    { text: "Complete the full 5-day course even if symptoms improve" },
    { text: "Symptoms should start to improve within 72 hours — seek medical advice if not improving or worsening" },
    { text: "Paracetamol or ibuprofen (OTC, weight-appropriate dose) should also be used regularly for pain" },
    { text: "Decongestants and antihistamines do NOT help with AOM symptoms" },
    { text: "Seek urgent help (999/A&E) if signs of sepsis or serious complications develop" },
    { text: "Children should be encouraged to swallow solid oral dose forms where possible", linkRefs: ["medicines-for-children-amoxicillin", "medicines-for-children-giving-capsules"] },
  ],
  outcomeLinkRefs: ["target-rti-leaflet", "nhs-ear-infection", "healthier-together-earache"],
};
